import { Suspense, useContext, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import { apiOrganization } from "src/services/Api/Organization/apiOrganization";
import { wrapPromise } from "go-core";
import { MoneyApi } from "go-core/api/types";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { LoadingContainer } from "go-core/components/Loading";
import ReportData from "go-report/reports";
import { reportParamsConverter } from "go-report/reports/services/report-service";
import { ReportConfig } from "go-report/reports/services/types";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext } from "go-segment/context";
import { convertFilterValuesToSnakeCase } from "../../../../../Reports/utils/reportUtils";

interface MatchParams {
	archiveId: string;
}

const listName = "ARCHIVE_REPORT";
const resourceType = "ARCHIVE_REPORT";

const Report = ({ resource }: { resource?: Record<string, any> }) => {
	const { t } = useTranslation();
	const segmentContext = useContext(SegmentContext);
	const { archiveId } = useParams<MatchParams>();
	const organization = useSelector(selectOrganization);

	if (!resource) return null;

	const data = resource.read();

	let config = {
		reportConfigId: "ARCHIVE_REPORT",
		aggregatePrefix: "summary",
		type: "bill",
		showPercent: true,
		groups: [
			{
				id: "PRODUCT",
				name: t("common.word.product"),
			},
			{
				id: "STOCK",
				name: t("common.word.stock"),
			},
			{
				id: "PRODUCT_CATEGORY",
				name: t("common.word.product_category"),
			},
		],
		selectedGroups: ["PRODUCT"],
		columns: [
			{
				id: "initial_state",
				name: t("common.word.amount_initial"),
			},
			{
				id: "actual_state",
				name: t("common.word.amount_actual"),
			},
			{
				id: "diff_state",
				name: t("common.word.diff_state"),
			},
			{
				id: "initial_total_price_money",
				name: t("common.word.initial_price_money"),
				render: (item: MoneyApi) => FormatMoney(item),
			},
			{
				id: "actual_total_price_money",
				name: t("common.word.actual_price_money"),
				render: (item: MoneyApi) => FormatMoney(item),
			},
			{
				id: "diff_total_price_money",
				name: t("common.word.diff_price_money"),
				render: (item: MoneyApi) => FormatMoney(item),
			},
		],
		selectedColumns: [
			"initial_state",
			"actual_state",
			"diff_state",
			"initial_total_price_money",
			"actual_total_price_money",
			"diff_total_price_money",
		],
		filters: [
			{
				id: "product_ids",
				name: t("common.word.product"),
				type: "search_select",
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						apiOrganization.getProductSearchSelect(search, params, {
							cancelToken: options?.token,
						}),
				},
			},
			{
				id: "category_ids",
				name: t("common.word.product_category"),
				type: "search_select",
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						apiOrganization.getProductCategorySearchSelect(search, params, {
							cancelToken: options?.token,
						}),
				},
			},
			{
				id: "stock_ids",
				name: t("common.word.stock"),
				type: "search_select",
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						apiOrganization.getStockSearchSelect(search, params, {
							cancelToken: options?.token,
						}),
				},
			},
			{
				id: "actual_state",
				name: t("common.word.state"),
				type: "number",
			},
			{
				id: "initial_total_price_money",
				name: t("common.word.initial_price_money"),
				type: "number",
			},
			{
				id: "actual_total_price_money",
				name: t("common.word.actual_price_money"),
				type: "number",
			},
		],
		segments: [
			{
				id: "all",
				name: t("common.word.all", { ns: "lib" }),
				slug: "all",
			},
		],
		selectedSegment: "all",
		fetch: async (params: Record<string, any> = {}, sourceToken?: CancelTokenSource) => {
			delete params.chart_type;
			return Promise.all([
				apiOrganization.getArchive(archiveId, reportParamsConverter(params), {
					cancelToken: sourceToken?.token,
				}),
			]);
		},
		saveSegment: async (segment: SegmentType) => {
			return segmentContext.save(listName, resourceType, segment);
		},
		exportConfig: {
			title: t("modules.archives.header.title"),
			filename: t("modules.archives.header.title"),
			organization: organization.name,
		},
	} as ReportConfig;
	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...data.fields] : data.fields,
		filterValues: data.filter_values,
	};

	data.filter_values = convertFilterValuesToSnakeCase(data.filter_values || []);

	return <ReportData config={config} />;
};

export const ArchiveReport = () => {
	const [resource, setResource] = useState<Record<string, any>>();
	const segmentContextValue = useContext(SegmentContext);
	const { t } = useTranslation();
	const { handleChangeTabTitle } = useBrowserTabTitle();

	useEffect(() => {
		handleChangeTabTitle(t("modules.archives.header.title"));
		setResource(wrapPromise(segmentContextValue.get(listName, resourceType)));
	}, []);

	return (
		<Suspense fallback={<LoadingContainer />}>
			<Report resource={resource} />
		</Suspense>
	);
};
